<!-- Shared button — the Lybits "CdsBtn": a fully-rounded pill, compact,
     no scale-on-press (color steps darker on hover). Renders as <button>,
     a <RouterLink> (pass `to`), or an <a> (pass `href`). -->
<template>
  <component
    :is="tag"
    :to="to"
    v-bind="tag === 'a' ? { href } : {}"
    :type="tag === 'button' ? type : undefined"
    :disabled="tag === 'button' ? isDisabled : undefined"
    :aria-disabled="tag !== 'button' && isDisabled ? 'true' : undefined"
    :aria-busy="loading || undefined"
    :class="classes"
  >
    <span
      v-if="loading"
      class="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
      aria-hidden="true"
    ></span>
    <slot name="icon" />
    <slot />
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';

const props = withDefaults(
  defineProps<{
    variant?: 'primary' | 'outline' | 'ghost' | 'danger';
    size?: 'sm' | 'md';
    type?: 'button' | 'submit' | 'reset';
    to?: string | Record<string, unknown>;
    href?: string;
    disabled?: boolean;
    loading?: boolean;
    block?: boolean;
  }>(),
  { variant: 'primary', size: 'md', type: 'button' },
);

const tag = computed(() => (props.to ? RouterLink : props.href ? 'a' : 'button'));
const isDisabled = computed(() => props.disabled || props.loading);

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-full font-medium transition-colors ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas ' +
  'disabled:cursor-not-allowed disabled:opacity-50';
const SIZES: Record<string, string> = {
  sm: 'px-3 py-1 text-xs',
  md: 'px-4 py-2 text-sm',
};
const VARIANTS: Record<string, string> = {
  primary: 'bg-brand-600 text-white shadow-sm hover:bg-brand-700 active:bg-brand-800',
  outline: 'border border-line-strong bg-surface text-gray-700 hover:bg-gray-100 hover:text-ink',
  ghost: 'text-gray-700 hover:bg-gray-100 hover:text-ink',
  danger: 'bg-danger text-white shadow-sm hover:brightness-95',
};

const classes = computed(() => [
  BASE,
  SIZES[props.size],
  VARIANTS[props.variant],
  props.block && 'w-full',
  // links can't use :disabled — dim + block pointer events instead.
  tag.value !== 'button' && isDisabled.value && 'pointer-events-none opacity-50',
]);
</script>
